import { PROMO_CAMPAIGN_STORAGE_KEY } from './constants/storage';

/**
 * Remembers which promo campaign a golfer arrived through, such as the QR code behind
 * `/promo/end-of-season`. Signing up leaves the app (the hosted sign-in page, the confirmation
 * email) and comes back through a different route, so the offer is kept in localStorage until the
 * golfer finishes onboarding or the campaign ends.
 */
const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{0,63}$/i;

const listeners = new Set<() => void>();
const notifyListeners = () => listeners.forEach((listener) => listener());

/**
 * Calls `listener` whenever the remembered campaign changes in this tab (or another one), so screens
 * that depend on it can react to a golfer accepting an offer after they have already loaded.
 */
export const subscribeToPromoCampaign = (listener: () => void): (() => void) => {
  listeners.add(listener);
  window.addEventListener('storage', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', listener);
  };
};

export const rememberPromoCampaign = (slug: string): void => {
  if (!SLUG_PATTERN.test(slug)) return;
  try {
    localStorage.setItem(PROMO_CAMPAIGN_STORAGE_KEY, slug);
  } catch {
    // Private mode or full storage: the offer is simply not remembered across sign-in.
  }
  notifyListeners();
};

export const getRememberedPromoCampaign = (): string | null => {
  try {
    const slug = localStorage.getItem(PROMO_CAMPAIGN_STORAGE_KEY);
    return slug && SLUG_PATTERN.test(slug) ? slug : null;
  } catch {
    return null;
  }
};

export const clearPromoCampaign = (): void => {
  try {
    localStorage.removeItem(PROMO_CAMPAIGN_STORAGE_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
  notifyListeners();
};
